import React, { FC } from 'react';
import styled from 'styled-components/macro';
import { CHART_CONSTANTS } from '../constants';
import { TGateValues } from '../types';
import { TDistanceUnit, TVerticalDistanceUnit } from '@ems/client-design-system';

interface DeviationChartLegendProps {
  translations: {
    gateDirection: string;
    gateDirectionSame: string;
    gateDirectionOpposite: string;
    gateWidth: string;
    floorHeight: string;
    ceilingHeight: string;
    gateValues: string;
  };
  gateValues: TGateValues | null;
  units: {
    vertical: TVerticalDistanceUnit;
    horizontal: TDistanceUnit;
  };
}

/**
 * Styled span that renders a circle matching the same direction marker in the chart
 */
export const SameDirectionMarker = styled.span`
  display: inline-block;
  width: ${CHART_CONSTANTS.POINT_RADIUS * 2}px;
  height: ${CHART_CONSTANTS.POINT_RADIUS * 2}px;
  border-radius: 50%;
  background-color: #666;
  flex-shrink: 0;
`;

/**
 * Styled span that renders a triangle matching the opposite direction marker in the chart
 */
export const OppositeDirectionMarker = styled.span`
  display: inline-block;
  width: ${CHART_CONSTANTS.POINT_RADIUS * 2}px;
  height: ${CHART_CONSTANTS.POINT_RADIUS * 2}px;
  background-color: #666;
  flex-shrink: 0;
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
`;

/**
 * Container for legend items with flex layout and gap
 */
export const LegendItemsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 10px;
`;

/**
 * Container for each legend item (shape + text) with flex to center vertically
 */
export const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/**
 * Displays a legend for the gate deviation chart markers
 */
export const DeviationChartLegend: FC<DeviationChartLegendProps> = ({
  translations,
  gateValues,
  units,
}) => (
  <div style={{ paddingLeft: '55px', display: 'flex', flexDirection: 'row', gap: '20px' }}>
    <div>
      <h5 style={{ marginBottom: '10px' }}>{translations.gateDirection}</h5>
      <LegendItemsContainer>
        <LegendItem>
          <SameDirectionMarker />
          {translations.gateDirectionSame}
        </LegendItem>
        <LegendItem>
          <OppositeDirectionMarker />
          {translations.gateDirectionOpposite}
        </LegendItem>
      </LegendItemsContainer>
    </div>

    {gateValues && (
      <div>
        <h5 style={{ marginBottom: '10px' }}>{translations.gateValues}</h5>
        <LegendItemsContainer>
          <LegendItem>
            {translations.ceilingHeight}: {gateValues.ceilingAltitude} {units.vertical}
          </LegendItem>
          <LegendItem>
            {translations.floorHeight}: {gateValues.floorAltitude} {units.vertical}
          </LegendItem>
          <LegendItem>
            {translations.gateWidth}: {gateValues.gateWidth} {units.horizontal}
          </LegendItem>
        </LegendItemsContainer>
      </div>
    )}
  </div>
);
